import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  OutlinedInput,
  Paper,
  TablePagination,
} from "@material-ui/core";
import {
  DataGrid,
  GridCellParams,
  GridFooterContainer,
  GridSortModel,
  GridValueFormatterParams,
} from "@material-ui/data-grid";
import React, { useCallback, useEffect, useState } from "react";
import { useDataProvider, useNotify } from "react-admin";
import ClearIcon from "@material-ui/icons/Clear";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";
import { StyledButton } from "../../../components/ListShowButton";

interface ICustomerCardTable {
  isAutoHeight?: boolean;
  isNotHavePagination?: boolean;
}

interface ICustomerShowButton {
  customerMbcId: string;
}

interface ISortModel {
  field: string;
  sort: string;
}

const useStyles = makeStyles({
  tableWrapper: { height: 400, width: "100%" },
  tableRoot: {
    border: 0,
  },
  label: {
    backgroundColor: "#ffffff",
    padding: "0 10px",
  },
  filterRoot: {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#EAEAEA",
    },
  },
  formControl: {
    margin: 10,
  },
});

const CustomerCardTable = ({
  isAutoHeight,
  isNotHavePagination,
}: ICustomerCardTable) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>();

  const [nextLink, setNextLink] = useState<string>(null);
  const [customerCards, setCustomerCards] = useState<ICustomerCard[]>([]);
  const [page, setPage] = useState<number>(0);
  const [baseData, setBaseData] = useState<IBaseDataTable[]>([]);
  const [disableNextPage, setDisableNextPage] = useState<boolean>(false);

  const [filteredNextLink, setFilteredNextLink] = useState<string>(null);
  const [filteredCustomerCards, setFilteredCustomerCards] = useState<
    ICustomerCard[]
  >([]);
  const [filteredPage, setFilteredPage] = useState<number>(0);
  const [filteredBaseData, setFilteredBaseData] = useState<IBaseDataTable[]>(
    []
  );
  const [disableFilteredNextPage, setDisableFilteredNextPage] =
    useState<boolean>(false);

  const [sortModels, setSortModels] = useState<GridSortModel>([
    { field: "balanceLCY", sort: "desc" },
  ]);

  const provider = useDataProvider();
  const notify = useNotify();
  const classes = useStyles();

  const CustomerShowButton = ({ customerMbcId }: ICustomerShowButton) => {
    const history = useHistory();

    const getParentId = (customerMbcId: string) => {
      provider
        .customGetOne(`mbc/parents/${customerMbcId}`)
        .then(({ data }: any) => {
          history.push(`/parents/${data.id}/show`);
        })
        .catch((error: any) => {
          if (error.status === 404) error.message = "Parent not found";

          notify(`Error : ${error.message}`, "error");
          setLoading(false);
        });
    };

    return (
      <StyledButton
        variant="outlined"
        onClick={() => getParentId(customerMbcId)}
      >
        Show Detail
      </StyledButton>
    );
  };

  const getCustomerCards = (sortModel: ISortModel) => {
    setLoading(true);
    provider
      .customGetList("customerCards", {
        filter: {
          CopySellToAddrToQteFrom: "Person",
          IsHaveOutstandingAmount: true,
        },
        sort: {
          field: sortModel.field,
          order: sortModel.sort,
        },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: 0,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setBaseData(base);
        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const getCustomerCardsWithNextLink = () => {
    setLoading(true);
    provider
      .customGetList("customerCards", {
        filter: { NextLink: nextLink },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: page,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setBaseData((oldBaseData: IBaseDataTable[]) => [
          ...oldBaseData,
          ...base,
        ]);

        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const findBaseDataByPage = () => {
    if (baseData.length > 0) {
      const findData = baseData.find(
        (item: IBaseDataTable) => item.page === page
      );

      if (findData) {
        setCustomerCards(findData.data);
        setNextLink(findData.nextUrl);
        setDisableNextPage(findData.nextUrl ? false : true);
      } else {
        getCustomerCardsWithNextLink();
      }
    }
  };

  useEffect(() => {
    getCustomerCards(sortModels[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    findBaseDataByPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, baseData]);

  const customerCardColumns = [
    {
      field: "no",
      headerName: "Customer Card No",
      type: "string",
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      flex: 1.5,
      filterable: false,
      sortable: false,
      allowResizing: true,
    },
    {
      field: "eMail",
      headerName: "Email",
      type: "string",
      flex: 1.5,
      filterable: false,
      sortable: false,
      allowResizing: true,
    },
    {
      field: "mobilePhoneNo",
      headerName: "Mobile No.",
      type: "string",
      flex: 1,
      filterable: false,
      sortable: false,
      allowResizing: true,
    },
    {
      field: "balanceLCY",
      headerName: "Total Outstanding",
      type: "number",
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams) =>
        `$ ${params.value.toLocaleString()}`,
      filterable: false,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 2,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: ({ row }: GridCellParams) => {
        return <CustomerShowButton customerMbcId={row.no} />;
      },
    },
  ];

  const LoadMoreFooter = () => {
    const handleChangePage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number
    ) => {
      setPage(newPage);
    };

    const handleChangeFilteredPage = (
      event: React.MouseEvent<HTMLButtonElement> | null,
      newPage: number
    ) => {
      setFilteredPage(newPage);
    };

    return (
      <GridFooterContainer>
        <div></div>

        <TablePagination
          component="div"
          count={
            filterValue?.length > 2
              ? filteredBaseData.reduce((acc: number, item: IBaseDataTable) => {
                  return acc + item.data.length;
                }, 0)
              : baseData.reduce((acc: number, item: IBaseDataTable) => {
                  return acc + item.data.length;
                }, 0)
          }
          nextIconButtonProps={{
            disabled: loading
              ? loading
              : filterValue?.length > 2
              ? disableFilteredNextPage
              : disableNextPage,
          }}
          page={filterValue?.length > 2 ? filteredPage : page}
          rowsPerPage={25}
          rowsPerPageOptions={[25]}
          onChangePage={
            filterValue?.length > 2
              ? handleChangeFilteredPage
              : handleChangePage
          }
        />
      </GridFooterContainer>
    );
  };

  const getFilteredCustomerCards = (filterValue: string) => {
    provider
      .customGetList("customerCards", {
        filter: {
          CustomerNo: filterValue,
        },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: 0,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setFilteredBaseData(base);
        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const getFilteredCustomerCardsWithNextLink = () => {
    setLoading(true);
    provider
      .customGetList("customerCards", {
        filter: { NextLink: filteredNextLink },
      })
      .then(({ data }: any) => {
        const base: IBaseDataTable[] = [
          {
            page: filteredPage,
            currentUrl: null,
            nextUrl: data.nextLink ? data.nextLink : null,
            data: data.values,
          },
        ];
        setFilteredBaseData((oldBaseData: IBaseDataTable[]) => [
          ...oldBaseData,
          ...base,
        ]);

        setLoading(false);
      })
      .catch((error: any) => {
        notify(`Error from MBC : ${error.message}`, "error");
        setLoading(false);
      });
  };

  const findFilteredBaseDataByPage = () => {
    if (filteredBaseData.length > 0) {
      const findData = filteredBaseData.find(
        (item: IBaseDataTable) => item.page === filteredPage
      );

      if (findData) {
        setFilteredCustomerCards(findData.data);
        setFilteredNextLink(findData.nextUrl);
        setDisableFilteredNextPage(findData.nextUrl ? false : true);
      } else {
        getFilteredCustomerCardsWithNextLink();
      }
    }
  };

  useEffect(() => {
    findFilteredBaseDataByPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPage, filteredBaseData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onFilterChange = useCallback(
    debounce((filterValue: string) => {
      getFilteredCustomerCards(filterValue);
    }, 1000),
    []
  );

  useEffect(() => {
    if (
      filterValue !== undefined &&
      filterValue !== "" &&
      filterValue?.length > 2
    ) {
      setLoading(true);
      onFilterChange(filterValue);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  const handleClearValue = () => {
    setFilterValue("");
  };

  const handleSortModelChange = (gridSortModel: GridSortModel) => {
    if (sortModels[0].sort !== gridSortModel[0].sort) {
      setSortModels(gridSortModel);
    }
  };

  useEffect(() => {
    getCustomerCards(sortModels[0]);
  }, [sortModels]);

  return (
    <Paper elevation={1}>
      <FormControl variant="outlined" className={classes.formControl}>
        <InputLabel htmlFor="outlined-adornment" className={classes.label}>
          Customer No
        </InputLabel>
        <OutlinedInput
          id="outlined-adornment"
          className={classes.filterRoot}
          value={filterValue}
          onChange={({ target }) => {
            console.log("TEST", target.value);
            setFilterValue(target.value);
          }}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear text input"
                onClick={handleClearValue}
                edge="end"
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>

      {isAutoHeight ? (
        <DataGrid
          className={classes.tableRoot}
          rows={filterValue?.length > 2 ? filteredCustomerCards : customerCards}
          columns={customerCardColumns}
          components={{
            Footer: isNotHavePagination ? null : LoadMoreFooter,
          }}
          filterMode="server"
          loading={loading}
          sortingMode="server"
          sortingOrder={["desc", "asc"]}
          sortModel={sortModels}
          onSortModelChange={handleSortModelChange}
          autoHeight
        ></DataGrid>
      ) : (
        <div className={classes.tableWrapper}>
          <DataGrid
            className={classes.tableRoot}
            rows={
              filterValue?.length > 2 ? filteredCustomerCards : customerCards
            }
            columns={customerCardColumns}
            components={{
              Footer: isNotHavePagination ? null : LoadMoreFooter,
            }}
            filterMode="server"
            loading={loading}
            disableColumnMenu
            sortingMode="server"
            sortingOrder={["desc", "asc"]}
            sortModel={sortModels}
            onSortModelChange={handleSortModelChange}
          ></DataGrid>
        </div>
      )}
    </Paper>
  );
};

export default CustomerCardTable;
